import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Card } from '@/components/ui'
import { useUserManual, CATEGORY_META, type ManualPendingUpdate } from '@/hooks/useUserManual'
import { useArcReader } from '@/hooks/useArcReader'
import { useThemeFinder } from '@/hooks/useThemeFinder'
import { recordButtonClick } from '@/lib/usageTelemetry'

/**
 * Home の「AIからのお知らせ」を横フリックで捌くためのカード群。
 *
 * 設計（2026-06-15 ユーザー確定モデル）:
 *  - ユーザーが書くのは日記だけ。AIの分析への操作は「承認・編集・却下」に集約。
 *  - ❶ あなたが決めること = pending_updates(manual_seed) を before→after で表示し、
 *    その場で これにする/ちがう/あとで（編集は控えめに ✎）。useUserManual を再利用。
 *  - ❷ AIが見つけた、あなたの変化 = arc/theme/self_doc の更新を 見た✓ / ここ違う→直す。
 *  - スワイプは「移動」のみ。承認・却下は必ずボタンで（誤操作で自分の言葉が勝手に変わる事故を防ぐ）。
 */

// ---------------------------------------------------------------------------
// FlickDeck — 横フリックで 1 枚ずつ見せる汎用デッキ
// ---------------------------------------------------------------------------

function FlickDeck<T>({
  items,
  renderItem,
  ariaLabel,
}: {
  items: T[]
  renderItem: (item: T, index: number) => ReactNode
  ariaLabel: string
}) {
  const [index, setIndex] = useState(0)
  const startX = useRef<number | null>(null)
  const count = items.length

  // データが減って index がはみ出したら末尾へ寄せる
  useEffect(() => {
    setIndex((i) => (i > count - 1 ? Math.max(0, count - 1) : i))
  }, [count])

  if (count === 0) return null
  const safeIndex = Math.min(index, count - 1)
  const go = (d: number) => setIndex(Math.max(0, Math.min(count - 1, safeIndex + d)))

  return (
    <div
      role="group"
      aria-roledescription="カード"
      aria-label={`${ariaLabel}（${count}件中 ${safeIndex + 1}件目）`}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); go(1) }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1) }
      }}
      onTouchStart={(e) => { startX.current = e.touches[0]?.clientX ?? null }}
      onTouchEnd={(e) => {
        if (startX.current == null) return
        const dx = (e.changedTouches[0]?.clientX ?? startX.current) - startX.current
        if (dx < -40) go(1)
        else if (dx > 40) go(-1)
        startX.current = null
      }}
      style={{ outline: 'none' }}
    >
      {renderItem(items[safeIndex], safeIndex)}

      {count > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, padding: '0 2px' }}>
          <button
            type="button"
            aria-label="前のカード"
            onClick={() => go(-1)}
            disabled={safeIndex === 0}
            style={navBtnStyle(safeIndex === 0)}
          >
            ‹
          </button>
          <div style={{ display: 'flex', gap: 6 }} aria-hidden="true">
            {items.map((_, i) => (
              <span
                key={i}
                style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: i === safeIndex ? 'var(--accent)' : 'var(--border)',
                  transition: 'background .15s',
                }}
              />
            ))}
          </div>
          <button
            type="button"
            aria-label="次のカード"
            onClick={() => go(1)}
            disabled={safeIndex === count - 1}
            style={navBtnStyle(safeIndex === count - 1)}
          >
            ›
          </button>
        </div>
      )}
    </div>
  )
}

function navBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    minWidth: 44, minHeight: 32,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: 'transparent', border: '1px solid var(--border)', borderRadius: 8,
    color: disabled ? 'var(--text3)' : 'var(--text2)',
    fontSize: 18, lineHeight: 1, cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.4 : 1, fontFamily: 'var(--font)',
  }
}

// ---------------------------------------------------------------------------
// ❶ ProposalFlick — あなたが決めること（取扱説明書の更新候補）
// ---------------------------------------------------------------------------

const KIND_HEADING = {
  fontSize: 10, fontWeight: 600, color: 'var(--accent2)',
  textTransform: 'uppercase' as const, letterSpacing: '.08em', marginBottom: 8,
}

function ProposalFlick({ onContent }: { onContent: (has: boolean) => void }) {
  const { pending, loading, acceptUpdate, rejectUpdate, dismissUpdate, editProposalSeed } = useUserManual()
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<{ pendingId: number; seedIndex: number; draft: string } | null>(null)

  useEffect(() => {
    if (!loading) onContent(pending.length > 0)
  }, [loading, pending.length, onContent])

  if (loading || pending.length === 0) return null

  const renderCard = (p: ManualPendingUpdate, index: number) => {
    const seeds = p.proposed_content?.seeds ?? []
    const currentCards = (p.current_content?.cards ?? []).filter((c) => c.text?.trim())
    const hasBefore = currentCards.length > 0
    const categoryLabel = CATEGORY_META[p.category]?.label ?? p.category

    async function run(action: () => Promise<void>) {
      setBusy(true)
      try { await action() } finally { setBusy(false) }
    }

    async function saveEdit() {
      if (!editing) return
      const text = editing.draft.trim()
      if (text) await editProposalSeed(editing.pendingId, editing.seedIndex, text)
      setEditing(null)
    }

    return (
      <Card style={{ padding: 14, borderLeft: '3px solid var(--accent)', background: 'rgba(80, 70, 229, 0.04)' }}>
        {/* カテゴリ + 位置 */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 600 }}>
            あなたの取扱説明書 · {categoryLabel}
          </span>
          <span style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 0 }}>{index + 1} / {pending.length}</span>
        </div>

        {/* before（あれば） */}
        {hasBefore && (
          <>
            <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>今はこう</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
              {currentCards.map((c, i) => (
                <div key={i} style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
                  {c.text}
                </div>
              ))}
            </div>
            <div style={{ fontSize: 12, color: 'var(--accent2)', margin: '2px 0 8px' }}>↓ こう書き換えませんか</div>
          </>
        )}
        {!hasBefore && (
          <div style={{ fontSize: 12, color: 'var(--accent2)', marginBottom: 8 }}>新しく書き加えませんか</div>
        )}

        {/* after（提案） */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 6 }}>
          {seeds.map((s, i) => {
            const isEditingThis = editing?.pendingId === p.id && editing?.seedIndex === i
            if (isEditingThis) {
              return (
                <div key={i}>
                  <textarea
                    className="input"
                    value={editing!.draft}
                    onChange={(e) => setEditing({ pendingId: p.id, seedIndex: i, draft: e.target.value })}
                    onKeyDown={(e) => {
                      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); void saveEdit() }
                      if (e.key === 'Escape') { e.preventDefault(); setEditing(null) }
                    }}
                    autoFocus
                    rows={3}
                    style={{ fontSize: 14, width: '100%', boxSizing: 'border-box', lineHeight: 1.7, resize: 'vertical' }}
                  />
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}>
                    <button type="button" onClick={() => setEditing(null)} style={ghostBtn}>キャンセル</button>
                    <button type="button" onClick={() => void saveEdit()} style={ghostBtn}>保存</button>
                  </div>
                </div>
              )
            }
            return (
              <div key={i} style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.7, fontWeight: 500 }}>
                {s.text}
                {s.evidence && s.evidence.length > 0 && (
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4, fontWeight: 400 }}>
                    なぜ: {s.evidence.slice(0, 2).map((q) => `「${q}」`).join(' ')}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* 編集は控えめに（前面に出さない） */}
        {!editing && seeds.length > 0 && (
          <button
            type="button"
            onClick={() => setEditing({ pendingId: p.id, seedIndex: 0, draft: seeds[0].text })}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font)' }}
          >
            ✎ 自分の言葉に直す
          </button>
        )}

        {/* 操作 — スワイプではなく必ずボタンで */}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 12 }}>
          <button type="button" disabled={busy} onClick={() => run(() => dismissUpdate(p.id))} style={actionBtn('ghost')}>
            あとで
          </button>
          <button type="button" disabled={busy} onClick={() => run(() => rejectUpdate(p.id))} style={actionBtn('ghost')}>
            ちがう
          </button>
          <button type="button" disabled={busy} onClick={() => { recordButtonClick('home:proposal-accept'); void run(() => acceptUpdate(p.id)) }} style={actionBtn('primary')}>
            {busy ? '反映中…' : 'これにする'}
          </button>
        </div>
      </Card>
    )
  }

  return (
    <div>
      <div style={KIND_HEADING}>あなたが決めること</div>
      <FlickDeck items={pending} renderItem={renderCard} ariaLabel="あなたが決めること" />
    </div>
  )
}

// ---------------------------------------------------------------------------
// ❷ ChangesFlick — AIが見つけた、あなたの変化（arc / theme / self_doc）
// ---------------------------------------------------------------------------

const SEEN_PREFIX = 'fy_story_seen_'
const SELFDOC_SEEN_PREFIX = 'fy_selfdoc_seen_'

function isSeen(key: string): boolean {
  return localStorage.getItem(key) === '1'
}
function markSeen(key: string) {
  localStorage.setItem(key, '1')
}

interface ChangeItem {
  id: string
  eyebrow: string
  body: string
  seenKey: string | null
  /** true なら「ここ違う→直す」導線を出す（arc/theme は訂正できる） */
  editable: boolean
}

function ChangesFlick({ onContent }: { onContent: (has: boolean) => void }) {
  const navigate = useNavigate()
  const { arc, loading: arcLoading, updatedAt: arcUpdatedAt } = useArcReader()
  const { theme, loading: themeLoading, updatedAt: themeUpdatedAt } = useThemeFinder()
  const [headline, setHeadline] = useState<string | null>(null)
  const [headlineLoaded, setHeadlineLoaded] = useState(false)
  // 既読操作で即時に消すための再描画トリガー
  const [, force] = useState(0)

  useEffect(() => {
    supabase
      .from('self_doc')
      .select('headline')
      .eq('is_current', true)
      .order('version', { ascending: false })
      .limit(1)
      .then(({ data }) => {
        const h = (data?.[0] as { headline: string | null } | undefined)?.headline ?? null
        setHeadline(h && h.trim() ? h.trim() : null)
        setHeadlineLoaded(true)
      })
  }, [])

  const loading = arcLoading || themeLoading || !headlineLoaded

  const items: ChangeItem[] = []
  if (theme && themeUpdatedAt) {
    const key = SEEN_PREFIX + 'theme_' + themeUpdatedAt
    if (!isSeen(key)) {
      items.push({
        id: 'theme',
        eyebrow: 'あなたのテーマ',
        body: theme.change_summary ?? theme.identity ?? '',
        seenKey: key,
        editable: true,
      })
    }
  }
  if (arc && arcUpdatedAt) {
    const key = SEEN_PREFIX + 'arc_' + arcUpdatedAt
    if (!isSeen(key)) {
      items.push({
        id: 'arc',
        eyebrow: '最近の変化',
        body: arc.change_summary ?? arc.narrative ?? '',
        seenKey: key,
        editable: true,
      })
    }
  }
  if (headline) {
    const key = SELFDOC_SEEN_PREFIX + headline
    if (!isSeen(key)) {
      items.push({
        id: 'selfdoc',
        eyebrow: '今週のまとめ',
        body: headline,
        seenKey: key,
        editable: false,
      })
    }
  }
  const visible = items.filter((it) => it.body)

  useEffect(() => {
    if (!loading) onContent(visible.length > 0)
  }, [loading, visible.length, onContent])

  if (loading || visible.length === 0) return null

  const renderCard = (it: ChangeItem) => (
    <Card style={{ padding: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>
        {it.eyebrow}
      </div>
      <div style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.8 }}>{it.body}</div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 12 }}>
        <button
          type="button"
          onClick={() => { recordButtonClick('home:change-detail'); navigate('/partner?tab=analysis') }}
          style={actionBtn('ghost')}
        >
          {it.editable ? 'ここ違う→直す' : 'くわしく見る'}
        </button>
        {it.seenKey && (
          <button
            type="button"
            onClick={() => { if (it.seenKey) markSeen(it.seenKey); force((n) => n + 1) }}
            style={actionBtn('primary')}
          >
            見た ✓
          </button>
        )}
      </div>
    </Card>
  )

  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>
        AIが見つけた、あなたの変化
      </div>
      <FlickDeck items={visible} renderItem={renderCard} ariaLabel="AIが見つけた、あなたの変化" />
    </div>
  )
}

// ---------------------------------------------------------------------------
// shared button styles
// ---------------------------------------------------------------------------

const ghostBtn: React.CSSProperties = {
  fontSize: 11, padding: '4px 10px', background: 'transparent',
  border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text2)',
  cursor: 'pointer', fontFamily: 'var(--font)',
}

function actionBtn(kind: 'ghost' | 'primary'): React.CSSProperties {
  if (kind === 'primary') {
    return {
      minHeight: 36, fontSize: 12, fontWeight: 600, padding: '6px 14px',
      background: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 8,
      color: '#fff', cursor: 'pointer', fontFamily: 'var(--font)',
    }
  }
  return {
    minHeight: 36, fontSize: 12, padding: '6px 12px',
    background: 'transparent', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--text3)', cursor: 'pointer', fontFamily: 'var(--font)',
  }
}

export { ProposalFlick, ChangesFlick }
